"use client"

import * as React from "react"
import { format, parseISO, isSameDay } from "date-fns"
import { cs } from "date-fns/locale"
import { Lock, Clock, ChevronLeft, ChevronDown, Search } from "lucide-react"
import { motion } from "framer-motion"

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { Button } from "../../components/ui/button"
import { ScrollArea } from "../../components/ui/scroll-area"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import type { PrijmaciRizeni, Cas } from "./types"
import Alert from "../../components/Alert"
import { toast } from "@/src/components/ui/use-toast"

interface TimeSlotModalProps {
  isOpen: boolean
  onClose: () => void
  date: string
  prijmaciRizeni: PrijmaciRizeni
  oborId: string
  selectedCity: string
  toaster: () => void
}

const OBOR_COLORS = {
  hudebni: {
    primary: '#4f7971',
    light: '#e8f0ef',
    dark: '#3a5a54'
  },
  tanecni: {
    primary: '#96522b',
    light: '#f5ebe5',
    dark: '#7a4222'
  },
  vytvarny: {
    primary: '#d4b682',
    light: '#f9f5ed',
    dark: '#c5a46e'
  }
}

const INSTRUMENTS = [
  { emoji: "🎺", name: "Baskřídlovka" },
  { emoji: "🎸", name: "Basová kytara" },
  { emoji: "🥁", name: "Bicí" },
  { emoji: "🎸", name: "Elektronická kytara" },
  { emoji: "🎹", name: "Elektronické klávesy" },
  { emoji: "🎻", name: "Housle" },
  { emoji: "🎷", name: "Klarinet" },
  { emoji: "🎹", name: "Klavír" },
  { emoji: "🎸", name: "Kytara" },
  { emoji: "🎶", name: "Příčná flétna" },
  { emoji: "🎶", name: "Zobcová flétna" },
  { emoji: "🎷", name: "Saxofon" },
  { emoji: "🎺", name: "Trumpeta" },
  { emoji: "🎻", name: "Violoncello" },
  { emoji: "🎻", name: "Kontrabas" },
  { emoji: "🧑‍🎤", name: "Zpěv"}
]

export function TimeSlotModal({ isOpen, onClose, date, prijmaciRizeni, oborId, selectedCity, toaster }: TimeSlotModalProps) {
  const [selectedTimeSlot, setSelectedTimeSlot] = React.useState<Cas | null>(null)
  const [name, setName] = React.useState("")
  const [phone, setPhone] = React.useState("")
  const [email, setEmail] = React.useState("")
  const [birthDate, setBirthDate] = React.useState("")
  const [nastroj, setNastroj] = React.useState("")
  const [showInstrumentDropdown, setShowInstrumentDropdown] = React.useState(false)
  const [filteredInstruments, setFilteredInstruments] = React.useState(INSTRUMENTS)
  const [instrumentSearch, setInstrumentSearch] = React.useState("")
  const [gdprConsent, setGdprConsent] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const allowedDomains = [
    "centrum.cz", "seznam.cz", "email.cz", "icloud.com", "gmail.com", "outlook.com",
    "hotmail.com", "yahoo.com", "aol.com", "protonmail.com", "zoho.com", "mail.com"
  ]

  const formattedDate = format(parseISO(date), "EEEE d. MMMM yyyy", {
    locale: cs,
  })

  const timeSlots = React.useMemo(() => {
    const selectedDay = prijmaciRizeni.dny.find(
      (den) => isSameDay(parseISO(den.datum), parseISO(date)) && den.mesto === selectedCity,
    )
    return selectedDay ? selectedDay.casy : []
  }, [prijmaciRizeni, date, selectedCity])

  const hasSingleIntervalSlot = timeSlots.length === 1 && timeSlots[0].type === "interval"

  const handleTimeSelect = (slot: Cas) => {
    setSelectedTimeSlot(slot)
  }

  const handleInstrumentSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const search = e.target.value.toLowerCase()
    setInstrumentSearch(search)
    setFilteredInstruments(
      INSTRUMENTS.filter(inst => 
        inst.name.toLowerCase().includes(search) || 
        inst.emoji.includes(search)
      )
    )
  }

  const handleInstrumentSelect = (instrument: string) => {
    setNastroj(instrument)
    setShowInstrumentDropdown(false)
    setInstrumentSearch("")
    setFilteredInstruments(INSTRUMENTS)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
  
    const emailInput = (e.target as HTMLFormElement).email.value
    const emailRegex = /^[^@\s]+@([^@\s.]+\.[^@\s.]+)$/
    const match = emailInput.match(emailRegex)
    if (!match || !allowedDomains.includes(match[1].toLowerCase())) {
      alert("Tato emailová adresa není podporována!")
      setEmail("")
      return
    }

    if (!gdprConsent) {
      alert("Musíte souhlasit se zpracováním osobních údajů!")
      return
    }
  
    if (!selectedTimeSlot) return
  
    setIsSubmitting(true)
    try {
      const requestBody = oborId === "hudebni" ? {
        jmeno: name,
        email: emailInput,
        mobil: phone,
        datum: birthDate,
        nastroj: nastroj,
        pid: selectedTimeSlot.pid,
        did: selectedTimeSlot.did,
        cid: selectedTimeSlot.id,
      } : {
        jmeno: name,
        email: emailInput,
        mobil: phone,
        datum: birthDate,
        nastroj: nastroj,
        pid: selectedTimeSlot.pid,
        did: selectedTimeSlot.did,
        cid: selectedTimeSlot.id,
      }

      const response = await fetch("https://preview.zushm.cz/api/prihlasky/prijmacirizeni/prijdu", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })
  
      if (!response.ok) {
        throw new Error("Failed to submit reservation")
      }
  
      onClose()
      window.location.reload()
    } catch (error) {
      onClose()
      toaster() //ERROR MESSAGE
    } finally {
      setIsSubmitting(false)
    }
  }

  const currentColor = OBOR_COLORS[oborId as keyof typeof OBOR_COLORS] || OBOR_COLORS.hudebni

  const InstrumentDropdown = () => (
    <div className="relative">
      <div 
        className="flex items-center justify-between p-2 border rounded-md cursor-pointer h-9"
        onClick={() => setShowInstrumentDropdown(!showInstrumentDropdown)}
        style={{ borderColor: currentColor.primary }}
      >
        <div className="flex items-center">
          {nastroj ? (
            <>
              <span className="text-lg mr-2">{nastroj.split(' ')[0]}</span>
              <span>{nastroj.split(' ').slice(1).join(' ')}</span>
            </>
          ) : (
            <span className="text-gray-400">Vyberte nástroj</span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform ${showInstrumentDropdown ? 'rotate-180' : ''}`} />
      </div>
  
      {showInstrumentDropdown && (
        <div
          className="absolute z-10 w-full mt-1 border rounded-md bg-white shadow-lg"
          style={{ borderColor: currentColor.primary }}
        >
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Hledat nástroj..."
                className="pl-8 h-9"
                value={instrumentSearch}
                onChange={handleInstrumentSearch}
                autoFocus
              />
            </div>
          </div>
          <ScrollArea className="h-60">
            {filteredInstruments.length > 0 ? (
              filteredInstruments.map((instrument) => (
                <div
                  key={instrument.name}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleInstrumentSelect(`${instrument.emoji} ${instrument.name}`)}
                >
                  <span className="text-xl">{instrument.emoji}</span>
                  <span>{instrument.name}</span>
                </div>
              ))
            ) : (
              <div className="p-3 text-center text-gray-500">
                Nenalezeny žádné nástroje
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={`max-w-[95vw] ${selectedTimeSlot ? 'sm:max-w-[400px]' : 'sm:max-w-[350px]'}`}
        style={{ borderTop: `4px solid ${currentColor.primary}` }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg">
            {selectedTimeSlot ? (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6" 
                  onClick={() => setSelectedTimeSlot(null)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>Rezervace termínu</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: currentColor.primary }}
                ></div>
                <span>Časy - {formattedDate} - {selectedCity}</span>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {!selectedTimeSlot ? (
          <ScrollArea className="max-h-[50vh]">
            <div className="space-y-2">
              {timeSlots.map((slot) => (
                <motion.div
                  key={slot.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`rounded-lg border p-3 ${
                    slot.zabrano && slot.type === "interval" ? "bg-gray-50" : `hover:border-[${currentColor.primary}] hover:bg-[${currentColor.light}]`
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {slot.type === "interval" ? (
                        <Lock className="h-4 w-4" style={{ color: currentColor.primary }} />
                      ) : (
                        <Clock className="h-4 w-4" style={{ color: currentColor.primary }} />
                      )}
                      <span className="font-medium text-sm">
                        {slot.od} - {slot.do}
                      </span>
                    </div>
                    {(!slot.zabrano || slot.type !== "interval") && (
                      <Button
                        size="sm"
                        onClick={() => handleTimeSelect(slot)}
                        style={{
                          backgroundColor: slot.type === "interval" || hasSingleIntervalSlot ? currentColor.primary : "#f3f4f6",
                          color: slot.type === "interval" || hasSingleIntervalSlot ? "#fff" : "#374151"
                        }}
                        className="hover:opacity-90 transition-opacity"
                      >
                        {slot.type === "interval" || hasSingleIntervalSlot ? "Rezervovat" : "Příjdu!"}
                      </Button>
                    )}
                    {slot.type === "interval" && slot.zabrano ? (
                      <span className="text-sm text-gray-500">Obsazeno</span>
                    ) : null}
                  </div>
                </motion.div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div 
              className="rounded-lg p-3 border"
              style={{ 
                backgroundColor: currentColor.light,
                borderColor: currentColor.primary
              }}
            >
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" style={{ color: currentColor.primary }} />
                <span className="font-medium text-sm">
                  {selectedTimeSlot.od} - {selectedTimeSlot.do} • {selectedCity}
                </span>
              </div>
              <div className="text-xs mt-1" style={{ color: currentColor.dark }}>
                {formattedDate}
              </div>
            </div>

            <div>
              <Label htmlFor="name" className="text-sm">Jméno a příjmení</Label>
              <Input 
                id="name" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                required 
                className="h-9"
              />
            </div>

            {oborId === "hudebni" && (
              <div>
                <Label className="text-sm">Hudební nástroj</Label>
                <InstrumentDropdown />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input 
                id="email" 
                type="email"
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                required 
                className="h-9"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-sm">Telefon</Label>
              <Input 
                id="phone" 
                type="tel"
                value={phone} 
                onChange={(e) => setPhone(e.target.value)} 
                required 
                className="h-9"
              />
            </div>
            <div>
              <Label htmlFor="birthDate" className="text-sm">Datum narození</Label>
              <Input
                id="birthDate"
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                required
                max={new Date().toISOString().split('T')[0]}
                className="h-9"
              />
            </div>

            <div className="flex items-center space-x-2 pt-1">
              <input
                id="gdprConsent"
                type="checkbox"
                checked={gdprConsent}
                onChange={(e) => setGdprConsent(e.target.checked)}
                required
                className="h-4 w-4 rounded border-gray-300"
                style={{ color: currentColor.primary }}
              />
              <Label htmlFor="gdprConsent" className="text-xs font-medium leading-none">
                Souhlasím se zpracováním osobních údajů
              </Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button 
                type="submit" 
                style={{ backgroundColor: currentColor.primary }}
                className="h-9 hover:opacity-90 transition-opacity" 
                disabled={isSubmitting || !gdprConsent || (oborId === "hudebni" && !nastroj)}
              >
                {isSubmitting ? "Odesílání..." : "Potvrdit rezervaci"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}